import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import LiveStrip from "../components/LiveStrip";
import ReviewsBar from "../components/ReviewsBar";
import "../styles/parkingVsTaxi.css";

export default function CheapestWayToHeathrow() {
  const DEFAULT_PICKUP = "GU15 1AA";
  const TERMINALS = [
    { label: "Heathrow T2 (TW6 1EW)", postcode: "TW6 1EW" },
    { label: "Heathrow T3 (TW6 1QG)", postcode: "TW6 1QG" },
    { label: "Heathrow T4 (TW6 3XA)", postcode: "TW6 3XA" },
    { label: "Heathrow T5 (TW6 2GA)", postcode: "TW6 2GA" },
  ];

  const [form, setForm] = useState({
    pickup_postcode: "",
    destination_postcode: "",
    passengers: 4,
    luggage: 2,
    pickup_datetime: "",
  });
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [pickupWarning, setPickupWarning] = useState("");
  const [pickupFocused, setPickupFocused] = useState(false);
  const pickupTimer = useRef(null);

  const [loading, setLoading] = useState(false);
  const [loadingSteps, setLoadingSteps] = useState([]);
  const [loadingSlow, setLoadingSlow] = useState(false);
  const loadingTimers = useRef([]);

  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const autoSubmitted = useRef(false);

  useEffect(() => {
    const nextMondayAt11 = () => {
      const now = new Date();
      const day = now.getDay();
      const daysToMon = (8 - day) % 7 || 7;
      const target = new Date(now);
      target.setDate(now.getDate() + daysToMon);
      target.setHours(11, 0, 0, 0);
      const pad = (n) => String(n).padStart(2, "0");
      return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
    };

    setForm((prev) => ({
      ...prev,
      pickup_postcode: DEFAULT_PICKUP,
      destination_postcode: TERMINALS[1].postcode,
      passengers: 4,
      luggage: 2,
      pickup_datetime: nextMondayAt11(),
    }));
  }, []);

  useEffect(() => {
    if (autoSubmitted.current) return;
    if (form.pickup_postcode && form.destination_postcode && form.pickup_datetime) {
      autoSubmitted.current = true;
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pickup_postcode, form.destination_postcode, form.pickup_datetime]);

  const isLikelyUkPostcode = (value) => {
    if (!value) return false;
    const norm = String(value).toUpperCase().trim();
    const pcRegex = /^([A-Z]{1,2}[0-9][0-9A-Z]?|[A-Z][0-9][A-Z]|[A-Z]{2}[0-9]{2})\s*[0-9][A-Z]{2}$/;
    return pcRegex.test(norm);
  };

  const fetchPickupSuggestions = (value) => {
    const v = String(value || "");
    if (pickupTimer.current) clearTimeout(pickupTimer.current);
    if (v.length < 2) {
      setPickupSuggestions([]);
      setPickupWarning("");
      return;
    }
    if (v.length >= 4 && !isLikelyUkPostcode(v)) {
      setPickupWarning("Please enter a valid UK postcode (e.g. W11 4JG).");
    } else {
      setPickupWarning("");
    }
    pickupTimer.current = setTimeout(async () => {
      try {
        const resp = await api.get("/api/postcodes/suggest", { params: { q: v, limit: 10 } });
        const suggestions = resp.data?.suggestions || [];
        setPickupSuggestions(
          suggestions.map((pc) => ({
            postcode: pc,
            label: "Suggested",
            code: null,
          }))
        );
      } catch (e) {
        setPickupSuggestions([]);
      }
    }, 180);
  };

  const resetLoadingIndicators = () => {
    loadingTimers.current.forEach((t) => clearTimeout(t));
    loadingTimers.current = [];
    setLoadingSteps([]);
    setLoadingSlow(false);
  };

  const startLoadingIndicators = () => {
    resetLoadingIndicators();
    const steps = [
      { id: "distance", label: "Checking distance & time", status: "done" },
      { id: "taxi", label: "Checking taxi & ride-hail", status: "pending" },
      { id: "tfl", label: "Checking TfL fares", status: "pending" },
      { id: "nr", label: "Checking National Rail fares", status: "pending" },
      { id: "compare", label: "Comparing cost & hassle", status: "pending" },
    ];
    setLoadingSteps(steps);
    loadingTimers.current.push(
      setTimeout(() => setLoadingSteps((prev) => prev.map((s) => (s.id === "taxi" ? { ...s, status: "done" } : s))), 500),
      setTimeout(() => setLoadingSteps((prev) => prev.map((s) => (s.id === "tfl" ? { ...s, status: "done" } : s))), 900),
      setTimeout(() => setLoadingSteps((prev) => prev.map((s) => (s.id === "nr" ? { ...s, status: "done" } : s))), 1300),
      setTimeout(() => setLoadingSteps((prev) => prev.map((s) => (s.id === "compare" ? { ...s, status: "done" } : s))), 1600),
      setTimeout(() => setLoadingSlow(true), 1800)
    );
  };

  const submit = async () => {
    if (pickupWarning || !isLikelyUkPostcode(form.pickup_postcode)) {
      setPickupWarning("Please enter a valid UK postcode (e.g. W11 4JG).");
      setError("Please enter a valid pickup postcode.");
      return;
    }
    setLoading(true);
    setError(null);
    startLoadingIndicators();

    try {
      const payload = {
        ...form,
        pickup_postcode: form.pickup_postcode.replace(/\(.*?\)/g, "").trim(),
        passengers: Math.min(8, Math.max(1, Number(form.passengers) || 1)),
        luggage: Math.min(8, Math.max(0, Number(form.luggage) || 0)),
        destination_locked: true,
      };
      const res = await api.post("/api/train/compare", payload);
      setResult(res.data);
    } catch (err) {
      setError("Failed to calculate comparison. Please try again.");
    }

    setLoading(false);
    resetLoadingIndicators();
  };

  const renderMoney = (v) => (v == null ? "—" : `£${Number(v).toFixed(2)}`);

  const apiData = result?.data || {};
  const options = apiData.options || [];
  const meta = apiData.meta || {};
  const journey = result?.journey || {};
  const providers = meta.providers || {};
  const legacyCosts = result?.costs || {};
  const tflUnavailable = (apiData.unavailable || []).find((u) => u.id === "tfl");
  const nrUnavailable = (apiData.unavailable || []).find((u) => u.id === "national_rail");
  const pax = journey?.passengers ?? form.passengers ?? 1;

  const taxiOption = options.find((o) => o.id === "taxi_ccg") || null;
  const rideHailRange = providers?.ride_hail?.range || providers?.ride_hail?.breakdown?.range || null;
  const rideHailMid = rideHailRange?.mid ?? providers?.ride_hail?.mid ?? null;
  const rideHailOption =
    options.find((o) => o.id === "ride_hail_estimate") ||
    (rideHailMid != null
      ? {
          id: "ride_hail_estimate",
          label: "Ride-hail (estimate)",
          mode: "taxi",
          total_cost_gbp: Number(rideHailMid),
          breakdown: { range: rideHailRange || providers.ride_hail },
        }
      : null);
  const blackCabRange = providers?.black_cab?.range || providers?.black_cab?.breakdown?.range || null;
  const blackCabMid = blackCabRange?.mid ?? providers?.black_cab?.mid ?? null;
  const blackCabOption =
    options.find((o) => o.id === "black_cab_estimate") ||
    (blackCabMid != null
      ? {
          id: "black_cab_estimate",
          label: "Black cab (estimate)",
          mode: "taxi",
          total_cost_gbp: Number(blackCabMid),
          breakdown: { range: blackCabRange || providers.black_cab, note: providers.black_cab_note || null },
        }
      : null);

  const tflOption =
    options.find((o) => o.id === "tfl") ||
    (legacyCosts?.train?.pricing_mode === "one_way_per_passenger" && legacyCosts?.train?.total != null
      ? {
          id: "tfl_legacy",
          label: "TfL (Elizabeth/Tube)",
          mode: "train",
          total_cost_gbp: Number(legacyCosts.train.total),
          breakdown: {
            fare_per_person_gbp: legacyCosts.train.per_person ?? null,
            duration_minutes: legacyCosts.train.duration_minutes ?? null,
            walk_minutes: journey?.total_walk_minutes ?? null,
            interchanges: journey?.interchanges ?? null,
            price_only_reasons: [],
            price_only_win: false,
          },
        }
      : null);

  const nrOption = options.find((o) => o.id === "national_rail") || null;

  const candidateOptions = [tflOption, nrOption, taxiOption, rideHailOption, blackCabOption].filter(Boolean);
  const rankedOptions = candidateOptions
    .filter((o) => typeof o.total_cost_gbp === "number" && !Number.isNaN(o.total_cost_gbp))
    .sort((a, b) => a.total_cost_gbp - b.total_cost_gbp);
  const cheapestOption = rankedOptions[0] || null;
  const runnerUp = rankedOptions[1] || null;

  const walkFor = (opt) => {
    if (!opt) return 0;
    if (opt.mode === "taxi") return 0;
    const walk = opt.breakdown?.walk_minutes ?? journey?.total_walk_minutes ?? 0;
    const access = opt.breakdown?.access_walk_mins || 0;
    const egress = opt.breakdown?.egress_walk_mins || 0;
    return walk || access + egress || 0;
  };
  const changesFor = (opt) => {
    if (!opt) return 0;
    if (opt.mode === "taxi") return 0;
    return opt.breakdown?.interchanges ?? journey?.interchanges ?? 0;
  };
  const durationFor = (opt) => {
    if (!opt) return null;
    return opt.breakdown?.duration_minutes ?? meta.taxi_duration_mins ?? journey?.duration_minutes ?? null;
  };

  const cheapestFriction = (opt) => {
    if (!opt) return { friction: false, reasons: [] };
    const reasons = [];
    const walk = walkFor(opt);
    const changes = changesFor(opt);
    const duration = durationFor(opt);
    const taxiDuration = meta.taxi_duration_mins;
    const priceReasons = opt.breakdown?.price_only_reasons || [];
    if (priceReasons.includes("includes_bus")) reasons.push("🚌 includes bus");
    if (walk >= 15 || priceReasons.includes("long_walk")) reasons.push(`🚶 ${walk || 15}+ min walk`);
    if (changes >= 3 || priceReasons.includes("many_changes")) reasons.push(`🔁 ${changes} changes`);
    if (duration && taxiDuration && duration > 1.5 * taxiDuration) reasons.push("⏱ slower than taxi");
    return { friction: reasons.length > 0, reasons };
  };

  const cheapestMeta = cheapestFriction(cheapestOption);
  const bestOverall = cheapestMeta.friction && runnerUp ? runnerUp : cheapestOption;
  const savingVsBest =
    cheapestOption && bestOverall && cheapestOption.id !== bestOverall.id
      ? Number((bestOverall.total_cost_gbp - cheapestOption.total_cost_gbp).toFixed(2))
      : null;

  const renderHero = () => {
    const medals = ["GOLD", "SILVER", "BRONZE"];
    const palette = {
      GOLD: { bg: "#0b132b", text: "#5eead4", border: "#14b8a6", emoji: "🥇" },
      SILVER: { bg: "#0f172a", text: "#e2e8f0", border: "#cbd5e1", emoji: "🥈" },
      BRONZE: { bg: "#0b132b", text: "#fb923c", border: "#fb923c", emoji: "🥉" },
    };

    const tiles = medals.map((medal, idx) => {
      const opt = rankedOptions[idx] || null;
      const paletteFor = palette[medal] || palette.GOLD;
      const duration = durationFor(opt);
      const walk = walkFor(opt);
      const hassle = (() => {
        if (!opt) return null;
        const changes = changesFor(opt);
        if (walk >= 20 || changes >= 2) return { label: "High", emoji: "🔴" };
        if (walk >= 10 || changes >= 1) return { label: "Medium", emoji: "🟠" };
        return { label: "Low", emoji: "🟢" };
      })();
      const perPerson =
        opt?.mode === "train" && opt?.breakdown?.fare_per_person_gbp != null
          ? `(${renderMoney(opt.breakdown.fare_per_person_gbp)} pp × ${pax})`
          : null;
      const cheapestBadge = cheapestOption && opt && opt.id === cheapestOption.id;

      return (
        <div
          key={medal}
          style={{
            border: `1px solid ${paletteFor.border}`,
            borderRadius: 14,
            padding: 14,
            background: paletteFor.bg,
            color: paletteFor.text,
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.5 }}>
            {paletteFor.emoji} {medal}
          </div>
          <div style={{ fontSize: 17, fontWeight: 800, marginTop: 6, color: "#fff" }}>{opt ? opt.label : "—"}</div>
          <div style={{ fontSize: 16, fontWeight: 800, marginTop: 4, color: "#fff" }}>
            {opt ? renderMoney(opt.total_cost_gbp) : "—"} {perPerson}
          </div>
          {cheapestBadge && <div style={{ marginTop: 4, fontSize: 12, color: "#5eead4" }}>💷 Cheapest price</div>}
          {opt && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6, fontSize: 12, color: "#e5e7eb" }}>
              <span>{duration ? `⏱ ${duration} min` : "⏱ —"}</span>
              <span>{walk ? `🚶 ${walk} min walk` : "🚪 Door-to-terminal"}</span>
              {hassle && <span>{hassle.emoji} {hassle.label} hassle</span>}
            </div>
          )}
        </div>
      );
    });

    const cheapestLine = cheapestOption
      ? `${cheapestOption.label} at ${renderMoney(cheapestOption.total_cost_gbp)}`
      : "Not available";
    const bestLine = bestOverall
      ? `${bestOverall.label} at ${renderMoney(bestOverall.total_cost_gbp)}`
      : "Not available";

    return (
      <div
        style={{
          borderRadius: 18,
          padding: 18,
          marginBottom: 16,
          color: "#fff",
          background: "linear-gradient(120deg, #0b132b 0%, #14b8a6 100%)",
          boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.4, textTransform: "uppercase", opacity: 0.9 }}>
          Cheapest way summary
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>
          Best overall: {bestLine}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6, opacity: 0.95 }}>
          Cheapest price: {cheapestLine}
          {cheapestMeta.friction && cheapestMeta.reasons.length > 0 && (
            <span style={{ color: "#fbbf24", display: "block", marginTop: 4 }}>
              Cheapest but slower: {cheapestMeta.reasons.join(" • ")}
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, marginTop: 6, opacity: 0.9 }}>
          Gold = best overall. Cheapest badge stays on the lowest fare, even if it’s bus-heavy.
          {Number.isFinite(savingVsBest) && bestOverall && cheapestOption && bestOverall.id !== cheapestOption.id && (
            <span style={{ display: "block", marginTop: 4 }}>
              🚩 Cheapest saves about £{Math.abs(savingVsBest)} vs best overall — check the hassle before picking it.
            </span>
          )}
        </div>
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
          }}
        >
          {tiles}
        </div>
      </div>
    );
  };

  const renderFareCards = () => {
    const optionRow = (title, opt, extra) => {
      if (!opt) return null;
      const range = opt.breakdown?.range;
      const note = opt.breakdown?.note;
      return (
        <div className="train-fare-row" style={{ padding: "10px 12px" }}>
          <div className="train-fare-label" style={{ fontWeight: 700, fontSize: 13 }}>{title}</div>
          <div className="train-fare-value strong" style={{ fontSize: 14 }}>
            {range ? `${renderMoney(range.min)} – ${renderMoney(range.max)}` : renderMoney(opt.total_cost_gbp)}
            {extra && <div className="train-fare-sub" style={{ fontSize: 12 }}>{extra}</div>}
            {note && <div className="train-fare-sub" style={{ fontSize: 12 }}>{note}</div>}
          </div>
        </div>
      );
    };

    const tflPerPerson =
      tflOption?.breakdown?.fare_per_person_gbp != null
        ? `(${renderMoney(tflOption.breakdown.fare_per_person_gbp)} per person × ${pax})`
        : null;
    const nrPerPerson =
      nrOption?.breakdown?.fare_per_person_gbp != null
        ? `(${renderMoney(nrOption.breakdown.fare_per_person_gbp)} per person × ${pax})`
        : null;

    return (
      <div className="train-fare-grid" style={{ gap: 12 }}>
        <div className="train-fare-group" style={{ background: "#f8fafc", borderRadius: 12, padding: 10, fontSize: 13 }}>
          <div className="train-fare-group-heading" style={{ marginBottom: 6, fontSize: 14 }}>🚆 Rail options</div>
          {optionRow("TfL (Elizabeth/Tube)", tflOption, tflPerPerson)}
          {optionRow("National Rail", nrOption, nrPerPerson)}
          {tflUnavailable && (
            <div className="train-fare-row muted-row" style={{ padding: "8px 10px", fontSize: 12 }}>
              <div className="train-fare-label">TfL availability</div>
              <div className="train-fare-value muted">{tflUnavailable.reason || "TfL fare not returned for this journey."}</div>
            </div>
          )}
          {nrUnavailable && (
            <div className="train-fare-row muted-row" style={{ padding: "8px 10px", fontSize: 12 }}>
              <div className="train-fare-label">National Rail availability</div>
              <div className="train-fare-value muted">{nrUnavailable.reason || "National Rail fare not returned."}</div>
            </div>
          )}
        </div>

        <div className="train-fare-group" style={{ background: "#f8fafc", borderRadius: 12, padding: 10, fontSize: 13 }}>
          <div className="train-fare-group-heading" style={{ marginBottom: 6, fontSize: 14 }}>🚖 Taxi &amp; Ride-hail</div>
          {optionRow("CheapCabGrab (fixed)", taxiOption)}
          {optionRow("Ride-hail (estimate)", rideHailOption)}
          {optionRow("Black cab (estimate)", blackCabOption)}
          <div className="train-fare-row muted-row" style={{ padding: "8px 10px", fontSize: 12 }}>
            <div className="train-fare-label">Note</div>
            <div className="train-fare-value muted">Taxi is door-to-terminal and includes Heathrow drop-off.</div>
          </div>
        </div>
      </div>
    );
  };

  const renderAtGlance = () => {
    if (!candidateOptions.length) return null;
    const rows = candidateOptions.map((opt) => {
      const duration = durationFor(opt);
      const walk = walkFor(opt);
      const changes = changesFor(opt);
      const hassle = (() => {
        if (walk >= 20 || changes >= 2) return "High";
        if (walk >= 10 || changes >= 1) return "Medium";
        return "Low";
      })();
      const noteParts = [];
      const reasons = opt.breakdown?.price_only_reasons || [];
      if (reasons.includes("includes_bus")) noteParts.push("Bus segments");
      if (reasons.includes("long_walk") || walk >= 15) noteParts.push("Long walk");
      if (reasons.includes("many_changes") || changes >= 3) noteParts.push("Several changes");
      if (opt.mode === "taxi") noteParts.push("Door-to-terminal");
      return {
        id: opt.id,
        label: opt.label === "TfL Rail" ? "TfL Rail" : opt.label,
        cost: renderMoney(opt.total_cost_gbp),
        duration: duration ? `${duration} min` : "—",
        walk: walk ? `${walk} min` : "—",
        changes,
        hassle,
        note: noteParts.join(" • "),
      };
    });

    return (
      <div style={{ marginBottom: 14, border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
        <div className="train-summary-bar" style={{ background: "#0b132b", color: "#fff" }}>Compare at a glance</div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr repeat(5, 1fr)", gap: 0, fontSize: 13 }}>
          <div style={{ padding: 10, background: "#f8fafc", fontWeight: 700 }}>Option</div>
          <div style={{ padding: 10, background: "#f8fafc", fontWeight: 700 }}>Total</div>
          <div style={{ padding: 10, background: "#f8fafc", fontWeight: 700 }}>Time</div>
          <div style={{ padding: 10, background: "#f8fafc", fontWeight: 700 }}>Walk</div>
          <div style={{ padding: 10, background: "#f8fafc", fontWeight: 700 }}>Changes</div>
          <div style={{ padding: 10, background: "#f8fafc", fontWeight: 700 }}>Notes</div>
          {rows.map((r, idx) => (
            <React.Fragment key={r.id}>
              <div style={{ padding: 10, background: idx % 2 ? "#fff" : "#f9fafb" }}>{r.label}</div>
              <div style={{ padding: 10, background: idx % 2 ? "#fff" : "#f9fafb" }}>{r.cost}</div>
              <div style={{ padding: 10, background: idx % 2 ? "#fff" : "#f9fafb" }}>{r.duration}</div>
              <div style={{ padding: 10, background: idx % 2 ? "#fff" : "#f9fafb" }}>{r.walk}</div>
              <div style={{ padding: 10, background: idx % 2 ? "#fff" : "#f9fafb" }}>{r.changes}</div>
              <div style={{ padding: 10, background: idx % 2 ? "#fff" : "#f9fafb" }}>{r.note}</div>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const renderLoading = () => {
    if (!loading && !loadingSteps.length) return null;
    return (
      <div className="parking-results" style={{ marginTop: 12 }}>
        <div className="train-summary-bar" style={{ background: "#0b132b", color: "#fff" }}>
          Crunching cheapest way to Heathrow…
        </div>
        <div style={{ padding: 12, background: "#fff", border: "1px solid #e5e7eb", borderTop: 0 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {loadingSteps.map((s) => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <span>{s.status === "done" ? "✅" : "⏳"}</span>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
          {loadingSlow && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#b45309" }}>
              Live fares can take a few seconds — thanks for waiting.
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>Cheapest Way to Heathrow (2026): Train vs Taxi</title>
        <meta
          name="description"
          content="Find the cheapest way to Heathrow: TfL, National Rail, taxi, ride-hail. Live fares, walking/changes, and a cheapest-but-warning badge."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/cheapest-way-to-heathrow" />
        <meta property="og:title" content="Cheapest Way to Heathrow (2026)" />
        <meta
          property="og:description"
          content="Compare TfL, National Rail, and taxi to Heathrow with live fares, hassle, and a cheapest vs best overall summary."
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/cheapest-way-to-heathrow" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Cheapest Way to Heathrow",
            url: "https://www.cheapcabgrab.com/cheapest-way-to-heathrow",
            description: "Cheapest way to Heathrow comparing TfL, National Rail, taxi and ride-hail.",
            inLanguage: "en-GB",
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What is the cheapest way to Heathrow?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Often a TfL route is the cheapest, but it can involve bus segments, long walks or many changes. We show the cheapest and best overall.",
                },
              },
              {
                "@type": "Question",
                name: "Do you include National Rail fares?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. National Rail fares use licensed data (Rail Data Marketplace) and are compared alongside TfL and taxi options.",
                },
              },
              {
                "@type": "Question",
                name: "Are taxi prices live?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "CheapCabGrab uses live preview pricing. Ride-hail and black cab are estimates.",
                },
              },
              {
                "@type": "Question",
                name: "Which Heathrow terminals can I pick?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "T2, T3, T4 and T5. Destination is locked to Heathrow to keep comparisons accurate.",
                },
              },
              {
                "@type": "Question",
                name: "What if the cheapest option is slow?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We flag bus-heavy routes, long walks, many changes or very slow journeys so you can pick the best overall option instead.",
                },
              },
            ],
          })}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      <div className="parking-header">
        <h1>Cheapest Way to Heathrow (2026)</h1>
        <p style={{ fontWeight: 700, color: "#111827", marginBottom: 6 }}>
          Shows the cheapest fare and the best overall option side-by-side.
        </p>
        <div style={{ fontSize: 12.5, color: "#475467", marginTop: 4, lineHeight: 1.6 }}>
          <div>Cheapest can be TfL bus-heavy or long-walk. We badge that and surface the best overall choice.</div>
          <div style={{ marginTop: 4 }}>Includes TfL, National Rail (RDM licensed), taxi, ride-hail, and black cab estimates.</div>
        </div>
        <div style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", background: "#ecfeff", border: "1px solid #a5f3fc", borderRadius: 999, fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
          Updated 1 Feb 2026 • Cheapest + Best overall
        </div>
      </div>

      <div className="parking-form">
        <div className="form-group">
          <label>Pickup postcode</label>
          <input
            className="parking-input"
            value={form.pickup_postcode}
            placeholder="e.g. GU15 1AA"
            onChange={(e) => {
              setForm({ ...form, pickup_postcode: e.target.value });
              fetchPickupSuggestions(e.target.value);
            }}
            onFocus={() => setPickupFocused(true)}
            onBlur={() => setTimeout(() => setPickupFocused(false), 120)}
          />
          {pickupWarning && <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 4 }}>{pickupWarning}</div>}
          {pickupFocused && pickupSuggestions.length > 0 && (
            <div className="suggestion-box">
              {pickupSuggestions.map((s, idx) => (
                <div
                  key={`${s.postcode}-${idx}`}
                  className="suggestion-item"
                  onClick={() => {
                    setForm({ ...form, pickup_postcode: s.postcode });
                    setPickupFocused(false);
                    setPickupSuggestions([]);
                  }}
                >
                  <div className="suggestion-main">{s.postcode}</div>
                  <div className="suggestion-sub">{s.label || "Suggested"}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Heathrow terminal</label>
          <select
            className="parking-input"
            value={form.destination_postcode}
            onChange={(e) => setForm({ ...form, destination_postcode: e.target.value })}
          >
            {TERMINALS.map((t) => (
              <option key={t.postcode} value={t.postcode}>
                {t.label}
              </option>
            ))}
          </select>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Destination locked: Heathrow only (T2–T5)</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
          <div className="form-group">
            <label>Passengers</label>
            <input
              type="number"
              className="parking-input"
              min="1"
              max="8"
              step="1"
              value={form.passengers}
              onChange={(e) =>
                setForm({
                  ...form,
                  passengers: e.target.value === "" ? "" : Math.min(8, Math.max(1, Number(e.target.value))),
                })
              }
              onBlur={() => {
                const num = Number(form.passengers);
                setForm({ ...form, passengers: Number.isNaN(num) ? 1 : Math.min(8, Math.max(1, num)) });
              }}
            />
          </div>

          <div className="form-group">
            <label>Luggage</label>
            <input
              type="number"
              className="parking-input"
              min="0"
              max="8"
              step="1"
              value={form.luggage}
              onChange={(e) =>
                setForm({
                  ...form,
                  luggage: e.target.value === "" ? "" : Math.min(8, Math.max(0, Number(e.target.value))),
                })
              }
              onBlur={() => {
                const num = Number(form.luggage);
                setForm({ ...form, luggage: Number.isNaN(num) ? 0 : Math.min(8, Math.max(0, num)) });
              }}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Pickup date & time</label>
          <input
            type="datetime-local"
            className="parking-input"
            value={form.pickup_datetime}
            onChange={(e) => setForm({ ...form, pickup_datetime: e.target.value })}
          />
        </div>

        <button onClick={submit} className="parking-btn">
          {loading ? "Comparing…" : "Find the cheapest way"}
        </button>
        {error && <p style={{ color: "red", marginTop: "10px" }}>{error}</p>}
      </div>

      {renderLoading()}

      {result && candidateOptions.length > 0 && (
        <div className="parking-results">
          {renderHero()}
          {renderAtGlance()}

          {(tflUnavailable || nrUnavailable) && (
            <div className="best-choice" style={{ background: "#fff7ed", borderLeft: "5px solid #fb923c" }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>⚠️ Some rail data unavailable.</div>
              <div style={{ fontSize: 14, lineHeight: 1.5 }}>
                {nrUnavailable && (
                  <div style={{ marginBottom: 6 }}>
                    National Rail fares unavailable for this journey. This can happen for certain routes, times, or advance fares.
                  </div>
                )}
                {tflUnavailable && (
                  <div style={{ marginBottom: 6 }}>
                    TfL fares not returned. TfL operates mainly in and around London and may not cover your postcode or selected time.
                  </div>
                )}
                Taxi comparisons remain accurate.
              </div>
            </div>
          )}

          <details className="train-collapsible" open>
            <summary className="train-summary-bar">💷 Fares (one way)</summary>
            <div className="train-collapsible-body">{renderFareCards()}</div>
          </details>

          <details className="train-collapsible" open>
            <summary className="train-summary-bar">😌 Hassle & walking</summary>
            <div className="train-collapsible-body">
              <div style={{ fontSize: 13, color: "#475467", marginBottom: 8 }}>
                Cheapest can still be high-hassle. We show walk, changes, and notes for each option.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px,1fr))", gap: 10 }}>
                {candidateOptions.map((opt) => {
                  const walk = walkFor(opt);
                  const changes = changesFor(opt);
                  const duration = durationFor(opt);
                  const hassle = (() => {
                    if (walk >= 20 || changes >= 2) return { label: "High", emoji: "🔴" };
                    if (walk >= 10 || changes >= 1) return { label: "Medium", emoji: "🟠" };
                    return { label: "Low", emoji: "🟢" };
                  })();
                  return (
                    <div key={opt.id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, background: "#fff" }}>
                      <div style={{ fontWeight: 800 }}>{opt.id === "taxi_ccg" ? "Taxi" : opt.label}</div>
                      <div style={{ fontSize: 13, marginTop: 4 }}>
                        {hassle.emoji} {hassle.label} hassle
                      </div>
                      <div style={{ fontSize: 12, color: "#475467", marginTop: 6 }}>
                        {walk ? `🚶 ${walk} min walk` : "🚪 Door-to-terminal"} • {changes ? `${changes} change` : "0 changes"} • {duration ? `${duration} min` : "—"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </details>

          <details className="train-collapsible">
            <summary className="train-summary-bar">ℹ️ How we calculate</summary>
            <div className="train-collapsible-body">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>TfL fare: live journey + surcharge where applicable.</li>
                <li>National Rail fares from licensed Rail Data Marketplace (live where available).</li>
                <li>CheapCabGrab uses live preview pricing; ride-hail/black cab are estimates.</li>
                <li>Cheapest badge stays on the lowest price; Gold highlights best overall (cost + hassle).</li>
                <li><a href="https://www.raildatamarketplace.dft.gov.uk/" target="_blank" rel="noopener noreferrer">CheapCabGrab Rail Data Marketplace partner profile</a>.</li>
              </ul>
            </div>
          </details>
        </div>
      )}

      <div style={{ marginTop: 16, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#f9fafb" }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>Quick tips</div>
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
          <li>Cheapest is often TfL, but can include bus + long walks — check the badge.</li>
          <li>Taxi is door-to-terminal and can beat rail for groups with luggage.</li>
          <li>National Rail is shown for credibility and off-peak/advance deals.</li>
        </ul>
      </div>

      <div style={{ marginTop: "32px" }}>
        <ReviewsBar />
      </div>
    </div>
  );
}
